import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import "./LogsList.css";

interface Log {
  _id: string;
  action: string;
  ip: string;
  userAgent: string;
  createdAt: string;
  userName: string;
  userRole: string;
  details?: any;
}

interface ApiResponse {
  success: boolean;
  logs: Log[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const LogsList = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState<string[]>([]);
  const [filterPeriod, setFilterPeriod] = useState<string>("all");

  useEffect(() => {
    fetchLogs();
  }, []);

  const filterLogsByPeriod = useCallback(() => {
    if (filterPeriod === "all") {
      setFilteredLogs(logs);
      return;
    }

    const now = new Date();
    let startDate = new Date();

    switch (filterPeriod) {
      case "today":
        startDate.setHours(0, 0, 0, 0);
        break;
      case "yesterday":
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "week":
        startDate.setDate(now.getDate() - 7);
        break;
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        break;
    }

    const filtered = logs.filter(log => {
      const logDate = new Date(log.createdAt);
      return logDate >= startDate;
    });

    setFilteredLogs(filtered);
  }, [logs, filterPeriod]);

  useEffect(() => {
    filterLogsByPeriod();
  }, [filterLogsByPeriod]);

  const fetchLogs = async () => {
    try {
      const response = await api.get<ApiResponse>("/megaadmin/logs/actions?limit=100&action=CONNEXION");
      if (response.data.success) {
        setLogs(response.data.logs);
        setFilteredLogs(response.data.logs);
      }
    } catch (error) {
      console.error("Erreur chargement logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLog = async (logId: string) => {
    if (!window.confirm("Supprimer ce log ?")) return;
    try {
      await api.delete(`/megaadmin/logs/${logId}`);
      fetchLogs();
    } catch (error) {
      console.error("Erreur suppression log:", error);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedLogs.length} logs sélectionnés ?`)) return;
    
    try {
      await Promise.all(selectedLogs.map(id => api.delete(`/megaadmin/logs/${id}`)));
      setSelectedLogs([]);
      fetchLogs();
    } catch (error) {
      console.error("Erreur suppression multiple:", error);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Supprimer TOUS les logs ? Cette action est irréversible.")) return;
    try {
      await api.delete("/megaadmin/logs/tous");
      fetchLogs();
      setSelectedLogs([]);
    } catch (error) {
      console.error("Erreur suppression tous les logs:", error);
    }
  };

  const handleSelectAll = () => {
    if (selectedLogs.length === filteredLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(filteredLogs.map(log => log._id));
    }
  };

  const handleSelectLog = (logId: string) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const formatDate = (date: string) => {
    try {
      return new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return date;
    }
  };

  const getPeriodLabel = (period: string) => {
    const labels: {[key: string]: string} = {
      all: "Tous",
      today: "Aujourd'hui",
      yesterday: "Hier",
      week: "7 derniers jours",
      month: "30 derniers jours"
    };
    return labels[period] || period;
  };

  if (loading) {
    return <div className="loading">Chargement des logs...</div>;
  }

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h2>Historique des connexions</h2>
        <div className="header-actions">
          <button 
            className="btn-delete-all"
            onClick={handleDeleteAll}
            title="Tout supprimer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>
            Tout supprimer
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="period-filter">
          <label>Période :</label>
          <select 
            value={filterPeriod} 
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="period-select"
          >
            <option value="all">Tous les logs</option>
            <option value="today">Aujourd'hui</option>
            <option value="yesterday">Hier</option>
            <option value="week">7 derniers jours</option>
            <option value="month">30 derniers jours</option>
          </select>
        </div>

        <div className="filter-summary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-.5-13v6l5.25 3.15.75-1.23-4.5-2.67V7h-1.5z" fill="currentColor"/>
          </svg>
          <span>{filteredLogs.length} log(s) - {getPeriodLabel(filterPeriod)}</span>
        </div>

        {selectedLogs.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedLogs.length} log(s) sélectionné(s)</span>
            <button 
              className="btn-delete-selected"
              onClick={handleDeleteSelected}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
              </svg>
              Supprimer la sélection
            </button>
          </div>
        )}
      </div>

      {filteredLogs.length === 0 ? (
        <p className="empty-message">Aucun log trouvé pour cette période</p>
      ) : (
        <table className="logs-table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}>
                <input 
                  type="checkbox"
                  checked={selectedLogs.length === filteredLogs.length && filteredLogs.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Date</th>
              <th>Utilisateur</th>
              <th>Rôle</th>
              <th style={{ width: '80px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <tr key={log._id} className={selectedLogs.includes(log._id) ? "selected" : ""}>
                <td>
                  <input 
                    type="checkbox"
                    checked={selectedLogs.includes(log._id)}
                    onChange={() => handleSelectLog(log._id)}
                  />
                </td>
                <td>{formatDate(log.createdAt)}</td>
                <td className="user-name">
                  <strong>{log.userName || "Nom inconnu"}</strong>
                </td>
                <td>
                  <span className={`role-badge ${log.userRole || "inconnu"}`}>
                    {log.userRole || "inconnu"}
                  </span>
                </td>
                <td>
                  <button 
                    className="btn-delete-single"
                    onClick={() => handleDeleteLog(log._id)}
                    title="Supprimer"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default LogsList;